import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import { UserSubscription, LineItemPrice } from './entities';
import { SubscriptionRestful } from './restful';
import { ingestLineItemPrice, ingestSubscription, ingestSubscriptions } from './ingress';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'subscriptions_error'
    | 'update_subscriptions_error'
    | 'create_subscription_error'
    | 'delete_subscriptions_error'
    | 'restart_subscriptions_error'
    | 'pause_subscription_error';
  type: 'error';
}

interface CreateSubscriptionsEvent {
  subscription: UserSubscription;
  type: 'create_subscriptions';
}

interface SubscriptionsEvent {
  subscriptions: UserSubscription[];
  type: 'get_subscriptions';
}

interface UpdateSubscriptionsEvent {
  type: 'update_subscriptions';
  price: LineItemPrice;
}

interface DeleteSubscriptionsEvent {
  subscriptions: UserSubscription[];
  uuid: string;
  type: 'delete_subscriptions';
}

interface RestartSubscriptionsEvent {
  subscription: UserSubscription;
  type: 'restart_subscriptions';
}

export type SubscriptionRequestEvent =
  | ErrorEvent
  | SubscriptionsEvent
  | UpdateSubscriptionsEvent
  | CreateSubscriptionsEvent
  | DeleteSubscriptionsEvent
  | RestartSubscriptionsEvent;

interface SubscriptionFunctions {
  getSubscriptions: SubscriptionRequest['getSubscriptions'];
}

export class SubscriptionRequest extends ExtendedListenableSubscribable<
  SubscriptionRequestEvent,
  SubscriptionFunctions
> {
  private restful: SubscriptionRestful;

  constructor(session: Session) {
    super();
    this.restful = new SubscriptionRestful(session);
  }

  public createSubscription = async (plan: string, trial: boolean, coupon?: string): SafePromise<UserSubscription> => {
    const subscription = await ingestSubscription(this.restful.createSubscription(plan, trial, coupon));
    if (subscription.err) {
      const errorMessage = (await (subscription.err.data as Response).json()).error;
      this.dispatch({
        error: errorMessage,
        errorType: 'create_subscription_error',
        type: 'error',
      });
      return { err: new SafeError(errorMessage, 'create_subscription') };
    } else {
      this.dispatch({
        subscription: subscription.ok,
        type: 'create_subscriptions',
      });
      return subscription;
    }
  };

  public getSubscriptions = async (): SafePromise<UserSubscription[]> => {
    const subscriptions = await ingestSubscriptions(this.restful.getSubscriptions());
    if (subscriptions.err) {
      this.dispatch({
        error: subscriptions.err,
        errorType: 'subscriptions_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        subscriptions: subscriptions.ok,
        type: 'get_subscriptions',
      });
    }
    return subscriptions;
  };

  public updateSubscription = async (
    uuid: string,
    selectedPlan: string,
    card?: string,
    coupon?: string,
    isFeatureUpdate?: boolean,
  ): SafePromise<LineItemPrice> => {
    const subscriptions = await ingestLineItemPrice(
      this.restful.updateSubscription(uuid, selectedPlan, card, coupon, isFeatureUpdate),
    );
    if (subscriptions.err) {
      this.dispatch({
        error: subscriptions.err,
        errorType: 'update_subscriptions_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        price: subscriptions.ok,
        type: 'update_subscriptions',
      });
    }
    return subscriptions;
  };

  public deleteSubscription = async (uuid: string): SafePromise<any> => {
    const subscriptions = await this.restful.deleteSubscription(uuid);
    if (subscriptions.err) {
      this.dispatch({
        error: subscriptions.err,
        errorType: 'delete_subscriptions_error',
        type: 'error',
      });
    } else {
      await this.getSubscriptions();
      this.dispatch({
        subscriptions: subscriptions.ok,
        type: 'delete_subscriptions',
        uuid: uuid,
      });
    }
    return subscriptions;
  };

  public restartSubscription = async (uuid: string): SafePromise<UserSubscription> => {
    const subscription = await this.restful.restartSubscription(uuid);
    if (subscription.err) {
      this.dispatch({
        error: subscription.err,
        errorType: 'restart_subscriptions_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        subscription: subscription.ok,
        type: 'restart_subscriptions',
      });
    }
    return subscription;
  };

  protected onSubscribe(): SubscriptionFunctions {
    return {
      getSubscriptions: this.getSubscriptions,
    };
  }
}
